import React from 'react';
import {
    useCloseShiftMutation,
    useCurrentShiftQuery,
    useOpenShiftMutation,
} from '@/api/queries/shifts';
import type {
    CloseShiftRequest,
    OpenShiftRequest,
} from '@/api/handlers/resources/shifts/requests';
import type { ShiftResponse } from '@/api/handlers/resources/shifts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { ShiftHandover, ShiftStatus, ShiftStatusRead } from './shiftMeta';
import { useSalePointHandover } from './useSalePointHandover';

export type ShiftContextValue = {
    status: ShiftStatus;
    isOpen: boolean;
    /** `GET /shifts/current` has not answered yet. */
    isLoading: boolean;
    /** Whether the status is known at all; `isOpen` is only meaningful once it is. */
    statusRead: ShiftStatusRead;
    shift: ShiftResponse | null;
    handover: ShiftHandover | null;
    /** `false` while the handover guard stands; a seller with a shift open closes it instead. */
    canOpenShift: boolean;
    openShift: (input: OpenShiftRequest) => Promise<ApiResponse<ShiftResponse>>;
    closeShift: (
        input: CloseShiftRequest,
    ) => Promise<ApiResponse<ShiftResponse>>;
};

const ShiftContext = React.createContext<ShiftContextValue | null>(null);

export const ShiftContextProvider = ShiftContext.Provider;

/** Consume the shift state. Throws if used outside `<ShiftProvider>`. */
export const useShift = (): ShiftContextValue => {
    const context = React.useContext(ShiftContext);
    if (!context) {
        throw new Error('useShift must be used within a ShiftProvider');
    }
    return context;
};

/**
 * Builds the shift open/close state over the **server** source of truth:
 * `GET /shifts/current` is the single authority for whether a shift is open
 * (it returns the open shift, or `null`/204 when none). `openShift`/`closeShift`
 * post the counted cash (+ a co-sign on a discrepancy) and resolve the raw
 * `ApiResponse` so the caller can branch on success; the mutations invalidate
 * the current-shift query, which flips `isOpen` on the refetch. The server
 * stamps seller/branch from the token and computes expected/discrepancy — the
 * client never sends them.
 *
 * It also carries the sale point handover guard (SEL-30, `useSalePointHandover`):
 * an open here is remembered on the device, and a close here forgets it.
 */
export const useShiftState = (): ShiftContextValue => {
    const currentQuery = useCurrentShiftQuery();
    const openMutation = useOpenShiftMutation();
    const closeMutation = useCloseShiftMutation();

    const shift: ShiftResponse | null =
        currentQuery.data && currentQuery.data.success
            ? currentQuery.data.response
            : null;
    const isOpen = shift !== null;
    const status: ShiftStatus = isOpen ? 'open' : 'closed';
    const isLoading = currentQuery.isLoading;

    // A refused read (a `success: false` envelope) or one that never arrived
    // leaves the status unknown, not "closed" (SEL-49). A read in flight with
    // no answer to show yet, first or a retry, is loading.
    const { refetch } = currentQuery;
    const retryStatusRead = React.useCallback(() => {
        void refetch();
    }, [refetch]);
    const isStatusKnown = currentQuery.data?.success === true;
    const isReading = currentQuery.isLoading || currentQuery.isFetching;
    const statusRead = React.useMemo<ShiftStatusRead>(() => {
        if (isStatusKnown) {
            return { state: 'known' };
        }
        return isReading
            ? { state: 'loading' }
            : { state: 'failed', onRetry: retryStatusRead };
    }, [isStatusKnown, isReading, retryStatusRead]);

    const { handover, canOpenShift, remember, forgetIfRemembered } =
        useSalePointHandover();

    const openShift = React.useCallback(
        async (input: OpenShiftRequest) => {
            const result = await openMutation.mutateAsync(input);
            if (result.success) {
                remember(result.response);
            }
            return result;
        },
        [openMutation, remember],
    );

    const closeShift = React.useCallback(
        async (input: CloseShiftRequest) => {
            const result = await closeMutation.mutateAsync(input);
            if (result.success) {
                forgetIfRemembered(result.response);
            }
            return result;
        },
        [closeMutation, forgetIfRemembered],
    );

    return React.useMemo(
        () => ({
            status,
            isOpen,
            isLoading,
            statusRead,
            shift,
            handover,
            canOpenShift,
            openShift,
            closeShift,
        }),
        [
            status,
            isOpen,
            isLoading,
            statusRead,
            shift,
            handover,
            canOpenShift,
            openShift,
            closeShift,
        ],
    );
};
